import { ReactElement, useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useElementTypes, useModelClickListeners, useTypeInteraction } from '@editorHooks'
import { CollapsibleProblemsBoxSection } from '@editorComponents'
import { ElementFormMode } from '../../../elementFormMode'
import Actions from './components/Actions'
import AddRoofForm from './components/AddRoofForm'
import RoofFormBase from './components/RoofFormBase'
import SlabFormBase from './components/SlabFormBase'

const roofTypeInteraction: ElementTypes[] = ['roof_slabs']
interface Props {
  selectedElement: string | null
  selectElement: (element: string | null) => void
  /** `architectural` hides the problems / warnings section — used by the IFC importer. */
  mode?: ElementFormMode
}

const RoofForm = ({ selectedElement, selectElement, mode = 'all' }: Props): ReactElement => {
  const elementTypes = useElementTypes()

  useTypeInteraction(roofTypeInteraction)

  useModelClickListeners(
    event => selectElement(event.object.name),
    [elementTypes],
    !selectedElement,
  )

  return (
    <>
      {!selectedElement ? (
        <Box component="div" p={1} border={1} borderColor="grey.200" borderRadius={1}>
          <Stack direction="column" spacing={1}>
            <Typography>Wählen Sie ein Dach an</Typography>
            <AddRoofForm />
          </Stack>
        </Box>
      ) : (
        <>
          <Stack direction="column" spacing={1}>
            <RoofFormBase
              selectedElement={selectedElement}
              setSelectedElement={selectElement}
              onClose={() => selectElement(null)}
            />
            <Actions selectedElement={selectedElement} />
            {mode === 'all' && <CollapsibleProblemsBoxSection elementGuid={selectedElement} />}
          </Stack>
        </>
      )}
    </>
  )
}

interface SlabFormProps {
  selectedElement: string | null
  selectElement: (element: string | null) => void
  elementType: 'roof_slabs' | 'slabs'
  /** `architectural` hides the problems / warnings section — used by the IFC importer. */
  mode?: ElementFormMode
}

const SlabForm = ({
  selectedElement,
  selectElement,
  elementType,
  mode = 'all',
}: SlabFormProps): ReactElement => {
  const elementTypes = useElementTypes()

  const slabTypeInteraction: ElementTypes[] = useMemo(() => {
    return [elementType]
  }, [elementType])

  useTypeInteraction(slabTypeInteraction)

  useModelClickListeners(
    event => selectElement(event.object.name),
    [elementTypes],
    !selectedElement,
  )

  return (
    <>
      {!selectedElement ? (
        <Box p={1} border={1} borderColor="grey.200" borderRadius={1}>
          <Typography>Wählen Sie eine Decke an</Typography>
        </Box>
      ) : (
        <>
          <SlabFormBase selectedElement={selectedElement} onClose={() => selectElement(null)} />
          {mode === 'all' && <CollapsibleProblemsBoxSection elementGuid={selectedElement} />}
        </>
      )}
    </>
  )
}

export { RoofForm, SlabForm }
